import { MutableRefObject } from 'react';

import { AbbWindchillConnector } from '../connector/AbbWindchillConnector';
import { InboundPartnr, PartNrMessage, Material, SurfaceTreatment, AssignmentMessage, MaterialColor, MaterialDimension, AlternateMaterial } from '../types/CommonTypes';
import { buildAssignment } from '../utils/buildAssignment';
import { ConditionType } from './usePartNrAssignment';
import { usePostMessageConnector } from './usePostMessageConnector';
import { useAssignment } from './useAssignment';
import Config from '../utils/config';

export function useAbbConnector(): {
    connector: MutableRefObject<AbbWindchillConnector | null>;
    sendAssignment: (partNrs: InboundPartnr[], material?: Material, surfaceTreatment?: SurfaceTreatment, condition?: MaterialColor | MaterialDimension | null, conditionType?: ConditionType | null, alternates?: AlternateMaterial[]) => void;
} {
    const assignment = useAssignment();
    const connector = usePostMessageConnector({
        factory: () => new AbbWindchillConnector(),
        initOptions: {
            targetOrigin: Config.get('toWindchillOrigin'),
            allowedOrigins: Config.get('fromWindchillOrigins'),
        },
        requiredMessageTypes: [
            { type: PartNrMessage.type, version: PartNrMessage.version },
            { type: AssignmentMessage.type, version: AssignmentMessage.version },
        ],
        onHandshakeComplete: (c) => {
            c.onMessage(PartNrMessage.type, (msg) => {
                const m = msg as PartNrMessage;
                assignment.setRawPartNrs(m.partNrs);
                const preloadedAlternates: AlternateMaterial[] = m.partNrs.flatMap(p =>
                    (p.alternates ?? []).map(a => ({
                        id: crypto.randomUUID(),
                        partNr: p.partnr,
                        materialId: a.bmId,
                        materialName: a.bmName ?? a.bmId,
                        abbId: a.abbId ?? undefined,
                        materialDensity: a.bmDensity,
                        conditionType: a.conditionType ?? null,
                        condition: a.condition ?? null,
                    }))
                );
                if (preloadedAlternates.length > 0) {
                    assignment.initAlternates(preloadedAlternates);
                }
            });
        },
    });

    function sendAssignment(partNrs: InboundPartnr[], material?: Material, surfaceTreatment?: SurfaceTreatment, condition?: MaterialColor | MaterialDimension | null, conditionType?: ConditionType | null, alternates?: AlternateMaterial[]): void {
        const c = connector.current;
        if (!c) return;
        const msg = new AssignmentMessage(buildAssignment(partNrs, material, surfaceTreatment, condition, conditionType), alternates ?? []);
        c.send(msg);
    }

    return { connector, sendAssignment };
}